import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import RefreshIcon from '@mui/icons-material/Refresh';
import DescriptionIcon from '@mui/icons-material/Description';
import ViewHeadlineIcon from '@mui/icons-material/ViewHeadline';
import { Select, MenuItem } from '@mui/material';
import SimpleModal from './SimpleModal';
import moment from 'moment';
import LogComponent from './LogComponent';
import MetaDataComponent from './MetaDataComponent';

import {
    Box,
    Button,
    CardContent,
    Checkbox,
    Grid,
    IconButton,
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    TextField,
    Toolbar,
    Tooltip,
    Typography
} from '@mui/material';
import Chip from 'ui-component/extended/Chip';

import MainCard from 'ui-component/cards/MainCard';
import { visuallyHidden } from '@mui/utils';
import DeleteIcon from '@mui/icons-material/Delete';

import SearchIcon from '@mui/icons-material/Search';

import { useTheme } from '@mui/material/styles';

const options = [
    { label: 'Tất cả thời gian', value: 'all' },
    { label: '7 ngày trước', value: '7' },
    { label: '30 ngày trước', value: '30' },
    { label: '90 ngày trước', value: '90' }
];

const sortOptions = [
    { label: 'Xếp theo mới nhất', value: 'desc' },
    { label: 'Xếp theo cũ nhất', value: 'asc' }
];
function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

const getComparator = (order, orderBy) =>
    order === 'desc' ? (a, b) => descendingComparator(a, b, orderBy) : (a, b) => -descendingComparator(a, b, orderBy);

function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

const headCells = [
    { id: 'id', numeric: true, label: 'STT', align: 'center' },
    { id: 'name', numeric: false, label: 'Tài liệu', align: 'left' },
    { id: 'type', numeric: true, label: 'Loại tài liệu', align: 'center' },
    { id: 'crawl_status', numeric: true, label: 'Crawl Status', align: 'center' },
    { id: 'index_status', numeric: true, label: 'Index Status', align: 'center' },
    { id: 'created_date', numeric: true, label: 'Thời gian cập nhật', align: 'center' },
    { id: 'word_num', numeric: false, label: 'Tổng số từ', align: 'center' }
    // { id: 'actions', numeric: false, label: 'Thao tác', align: 'center' } // Added this line for the "Thao tác" column
];

const EnhancedTableHead = ({ onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort, theme, selected }) => {
    const createSortHandler = (property) => (event) => {
        onRequestSort(event, property);
    };

    return (
        <TableHead>
            <TableRow>
                <TableCell padding="checkbox" sx={{ pl: 3 }}>
                    <Checkbox
                        color="primary"
                        indeterminate={numSelected > 0 && numSelected < rowCount}
                        checked={rowCount > 0 && numSelected === rowCount}
                        onChange={onSelectAllClick}
                        inputProps={{
                            'aria-label': 'select all desserts'
                        }}
                    />
                </TableCell>
                {numSelected > 0 && (
                    <TableCell padding="none" colSpan={8}>
                        <EnhancedTableToolbar numSelected={selected.length} />
                    </TableCell>
                )}
                {numSelected <= 0 &&
                    headCells.map((headCell) => (
                        <TableCell
                            key={headCell.id}
                            align={headCell.align}
                            padding={headCell.disablePadding ? 'none' : 'normal'}
                            sortDirection={orderBy === headCell.id ? order : false}
                            sx={{ minWidth: 150, padding: 0 }}
                        >
                            <TableSortLabel
                                active={orderBy === headCell.id}
                                direction={orderBy === headCell.id ? order : 'asc'}
                                onClick={createSortHandler(headCell.id)}
                                sx={{
                                    // fontSize: '0.1rem',
                                    fontWeight: 'bold'
                                }}
                            >
                                {headCell.label}
                                {orderBy === headCell.id ? (
                                    <Box component="span" sx={visuallyHidden}>
                                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                    </Box>
                                ) : null}
                            </TableSortLabel>
                        </TableCell>
                    ))}
                {/* <TableCell sortDirection={false} align="center" sx={{ pr: 3 }}>
                    <Typography variant="subtitle1" sx={{ color: theme.palette.mode === 'dark' ? 'grey.600' : 'grey.900' }}>
                        Thao tác
                    </Typography>
                </TableCell> */}
            </TableRow>
        </TableHead>
    );
};

EnhancedTableHead.propTypes = {
    theme: PropTypes.object,
    selected: PropTypes.array,
    numSelected: PropTypes.number.isRequired,
    onRequestSort: PropTypes.func.isRequired,
    onSelectAllClick: PropTypes.func.isRequired,
    order: PropTypes.oneOf(['asc', 'desc']).isRequired,
    orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired
};

const EnhancedTableToolbar = ({ numSelected, selected }) => (
    <Toolbar
        sx={{
            p: 0,
            pl: 1,
            pr: 1,
            ...(numSelected > 0 && {
                color: (theme) => theme.palette.secondary.main
            })
        }}
    >
        {numSelected > 0 ? (
            <Typography color="inherit" variant="body1">
                Đã chọn {numSelected}
            </Typography>
        ) : (
            <Typography variant="h6" id="tableTitle">
                Nutrition
            </Typography>
        )}
        <Box sx={{ flexGrow: 1 }} />
        {numSelected > 0 && (
            <Tooltip title="Delete">
                <IconButton size="small" color="error" onClick={() => selected.forEach((name) => this.handleDelete(name))}>
                    <DeleteIcon fontSize="medium" />
                </IconButton>
            </Tooltip>
        )}
    </Toolbar>
);

EnhancedTableToolbar.propTypes = {
    numSelected: PropTypes.number.isRequired
};

const TableUploadFile = ({ onClick, folderName, dataUploadFile, isSetting }) => {
    console.log(dataUploadFile);
    const [metaDataOpen, setMetaDataOpen] = useState(false);
    const [logOpen, setLogOpen] = useState(false);
    const [originalRows, setOriginalRows] = useState(dataUploadFile);
    const [hideTableUploadFile, setHideTableUploadFile] = useState(false);
    const [open, setOpen] = useState(false);
    const [sort, setSort] = useState('desc');
    const [timeRange, setTimeRange] = useState('all');
    const theme = useTheme();
    const dispatch = () => {};
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('calories');
    const [selected, setSelected] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [search, setSearch] = useState('');
    const [rows, setRows] = useState(dataUploadFile);

    const filterDataByTimeRange = (range) => {
        const currentDate = moment();

        if (range === '7') {
            const sevenDaysAgo = currentDate.clone().subtract(7, 'days');
            return dataUploadFile.filter(
                (row) => moment(row.created_date).isAfter(sevenDaysAgo) || moment(row.created_date).isSame(sevenDaysAgo)
            );
        } else if (range === '30') {
            const thirtyDaysAgo = currentDate.clone().subtract(30, 'days');
            return dataUploadFile.filter(
                (row) => moment(row.created_date).isAfter(thirtyDaysAgo) || moment(row.created_date).isSame(thirtyDaysAgo)
            );
        } else if (range === '90') {
            const ninetyDaysAgo = currentDate.clone().subtract(90, 'days');
            return dataUploadFile.filter(
                (row) => moment(row.created_date).isAfter(ninetyDaysAgo) || moment(row.created_date).isSame(ninetyDaysAgo)
            );
        } else if (range === 'all') {
            return dataUploadFile;
        } else {
            return [];
        }
    };

    const handleOpenMetaData = () => {
        setMetaDataOpen(true);
        setHideTableUploadFile(!hideTableUploadFile);
    };

    const handleFilter = () => {
        if (timeRange === 'all') {
            setRows(originalRows);
        } else {
            const filteredRows = filterDataByTimeRange(timeRange);
            setRows(filteredRows);
        }
    };
    useEffect(() => {
        handleFilter();
    }, [timeRange, originalRows, rows]);

    const handleTimeRangeChange = (e) => {
        setTimeRange(e.target.value);
        setRows(originalRows);
    };

    const handleDelete = (name) => {
        // Lọc ra các hàng không chứa name cần xóa
        // const newRows = rows.filter((row) => row.name !== name);
        // setOriginalRows(newRows);

        // // Xóa khỏi mảng đã chọn
        // setSelected(selected.filter((selectedTaiLieu) => selectedTaiLieu !== name));
        console.log('da xoa', name);
    };
    const handleSearch = (event) => {
        const newString = event?.target.value;
        setSearch(newString || '');

        if (newString) {
            const newRows = dataUploadFile.filter((row) => {
                let matches = true;

                const properties = ['name', 'type', 'crawl_status', 'index_status', 'created_date', 'num_word', 'id'];
                let containsQuery = false;

                properties.forEach((property) => {
                    if (row[property].toString().toLowerCase().includes(newString.toString().toLowerCase())) {
                        containsQuery = true;
                    }
                });

                if (!containsQuery) {
                    matches = false;
                }
                return matches;
            });
            setOriginalRows(newRows);
        } else {
            setOriginalRows(dataUploadFile);
        }
    };

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            if (selected.length > 0) {
                setSelected([]);
            } else {
                const newSelectedId = dataUploadFile.map((n) => n.name);
                setSelected(newSelectedId);
            }
            return;
        }
        setSelected([]);
    };

    const handleClick = (event, name) => {
        const selectedIndex = selected.indexOf(name);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, name);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
        }

        setSelected(newSelected);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event?.target.value, 10));
        setPage(0);
    };

    const isSelected = (name) => selected.indexOf(name) !== -1;
    const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

    const toggleLog = () => {
        setLogOpen(!logOpen);
        setHideTableUploadFile(!hideTableUploadFile);
    };
    const handleBack = () => {
        setLogOpen(false);
        setMetaDataOpen(false);
        setHideTableUploadFile(false);
    };
    return (
        <>
            {isSetting === false && (
                <>
                    {!hideTableUploadFile && (
                        <MainCard title={`Tất cả tài liệu / Tài liệu trợ lý ${folderName}`} content={false}>
                            <div>
                                <CardContent>
                                    <Grid container sx={{ justifyContent: 'flex-end' }}>
                                        <Grid item lg={3}>
                                            <Button variant="outlined" color="secondary" onClick={onClick}>
                                                Trở về
                                            </Button>
                                        </Grid>
                                        <Grid item lg={9} sx={{ textAlign: 'right' }}>
                                            <Select
                                                value={timeRange}
                                                onChange={handleTimeRangeChange}
                                                sx={{ width: '200px', marginRight: '10px' }}
                                            >
                                                {options.map((option) => (
                                                    <MenuItem key={option.value} value={option.value}>
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>

                                            <Select
                                                value={sort}
                                                onChange={(e) => {
                                                    setSort(e.target.value);
                                                    handleRequestSort(null, 'created_date');
                                                }}
                                                sx={{ width: '200px', marginRight: '10px' }}
                                            >
                                                {sortOptions.map((option) => (
                                                    <MenuItem key={option.value} value={option.value}>
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            <TextField
                                                sx={{ width: '300px', marginRight: '0' }}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <SearchIcon fontSize="medium" />
                                                        </InputAdornment>
                                                    )
                                                }}
                                                onChange={handleSearch}
                                                placeholder="Tìm kiếm"
                                                value={search}
                                                size="medium"
                                            />
                                        </Grid>
                                    </Grid>
                                </CardContent>

                                <TableContainer>
                                    <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle">
                                        <EnhancedTableHead
                                            numSelected={selected.length}
                                            order={order}
                                            orderBy={orderBy}
                                            onSelectAllClick={handleSelectAllClick}
                                            onRequestSort={handleRequestSort}
                                            rowCount={rows.length}
                                            theme={theme}
                                            selected={selected}
                                        />
                                        <TableBody>
                                            {stableSort(rows, getComparator(order, orderBy))
                                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                                .map((row, index) => {
                                                    const isItemSelected = isSelected(row.name);
                                                    const labelId = `enhanced-table-checkbox-${index}`;

                                                    return (
                                                        <TableRow
                                                            hover
                                                            role="checkbox"
                                                            aria-checked={isItemSelected}
                                                            tabIndex={-1}
                                                            key={index}
                                                            selected={isItemSelected}
                                                        >
                                                            <TableCell
                                                                padding="checkbox"
                                                                sx={{ pl: 3 }}
                                                                onClick={(event) => handleClick(event, row.name)}
                                                            >
                                                                <Checkbox
                                                                    color="primary"
                                                                    checked={isItemSelected}
                                                                    inputProps={{
                                                                        'aria-labelledby': labelId
                                                                    }}
                                                                />
                                                            </TableCell>
                                                            <TableCell
                                                                component="th"
                                                                id={labelId}
                                                                scope="row"
                                                                onClick={(event) => handleClick(event, row.name)}
                                                                sx={{ cursor: 'pointer', padding: 0, textAlign: 'center' }}
                                                            >
                                                                <Typography
                                                                    variant="subtitle1"
                                                                    sx={{ color: theme.palette.mode === 'dark' ? 'grey.600' : 'grey.900' }}
                                                                >
                                                                    {' '}
                                                                    {row.id}{' '}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell
                                                                component="th"
                                                                id={labelId}
                                                                scope="row"
                                                                onClick={(event) => handleClick(event, row.name)}
                                                                sx={{ cursor: 'pointer', padding: 0 }}
                                                            >
                                                                <Typography
                                                                    variant="subtitle1"
                                                                    sx={{ color: theme.palette.mode === 'dark' ? 'grey.600' : 'grey.900' }}
                                                                >
                                                                    {' '}
                                                                    {row.name}{' '}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell align="center" sx={{ padding: 0 }}>
                                                                {row.type === 'pdf' && (
                                                                    <Chip label="pdf" size="small" chipcolor="warning" />
                                                                )}
                                                                {row.type === 'docx' && (
                                                                    <Chip label="docx" size="small" chipcolor="primary" />
                                                                )}
                                                                {row.type === 'xml' && (
                                                                    <Chip label="xml" size="small" chipcolor="secondary" />
                                                                )}
                                                                {row.type === 'xlsx' && (
                                                                    <Chip label="xlsx" size="small" chipcolor="success" />
                                                                )}
                                                            </TableCell>
                                                            <TableCell align="center" sx={{ padding: 0 }}>
                                                                {row.crawl_status === 'Done' && (
                                                                    <Chip label="Done" size="small" chipcolor="success" />
                                                                )}
                                                                {row.crawl_status === 'Processing' && (
                                                                    <Chip label="Processing" size="small" chipcolor="primary" />
                                                                )}
                                                            </TableCell>
                                                            <TableCell align="center" sx={{ padding: 0 }}>
                                                                {row.index_status === 'Done' && (
                                                                    <Chip label="Done" size="small" chipcolor="success" />
                                                                )}
                                                                {row.index_status === 'Processing' && (
                                                                    <Chip label="Processing" size="small" chipcolor="primary" />
                                                                )}
                                                            </TableCell>
                                                            <TableCell align="center" sx={{ padding: 0 }}>
                                                                {row.created_date}
                                                            </TableCell>
                                                            <TableCell align="center" sx={{ padding: 0 }}>
                                                                {row.word_num}
                                                            </TableCell>
                                                            <TableCell align="center" sx={{ padding: 0, minWidth: 100 }}>
                                                                <Tooltip title="Tải lại">
                                                                    <IconButton color="primary" size="small" onClick={() => setOpen(true)}>
                                                                        <RefreshIcon />
                                                                    </IconButton>
                                                                </Tooltip>
                                                                <Tooltip title="Nhật ký">
                                                                    <IconButton
                                                                        color="primary"
                                                                        size="small"
                                                                        aria-label="log"
                                                                        onClick={toggleLog}
                                                                    >
                                                                        <DescriptionIcon sx={{ fontSize: '1.3rem' }} />
                                                                    </IconButton>
                                                                </Tooltip>
                                                                {logOpen && <LogComponent />}

                                                                <Tooltip title="Meta data">
                                                                    <IconButton
                                                                        color="primary"
                                                                        size="small"
                                                                        aria-label="data"
                                                                        onClick={handleOpenMetaData}
                                                                    >
                                                                        <ViewHeadlineIcon sx={{ fontSize: '1.3rem' }} />
                                                                    </IconButton>
                                                                </Tooltip>
                                                                {metaDataOpen && <MetaDataComponent />}
                                                                {/* <div>
                                                            {isItemSelected && (
                                                                <Tooltip title="Xóa dữ liệu">
                                                                    <span>
                                                                        <IconButton
                                                                            disabled={!isItemSelected}
                                                                            color="secondary"
                                                                            size="small"
                                                                            onClick={() => handleDelete(row.name)}
                                                                        >
                                                                            <DeleteIcon sx={{ fontSize: '1.3rem' }} />
                                                                        </IconButton>
                                                                    </span>
                                                                </Tooltip>
                                                            )}
                                                        </div> */}
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                            {emptyRows > 0 && (
                                                <TableRow
                                                    style={{
                                                        height: 53 * emptyRows
                                                    }}
                                                >
                                                    <TableCell colSpan={6} />
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                <TablePagination
                                    rowsPerPageOptions={[5, 10, 25]}
                                    component="div"
                                    count={rows.length}
                                    rowsPerPage={rowsPerPage}
                                    page={page}
                                    onPageChange={handleChangePage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                />
                                <SimpleModal open={open} closeModal={() => setOpen(false)} />
                            </div>
                        </MainCard>
                    )}
                </>
            )}

            {isSetting === true && (
                <div>
                    <TableContainer>
                        <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle">
                            <EnhancedTableHead
                                numSelected={selected.length}
                                order={order}
                                orderBy={orderBy}
                                onSelectAllClick={handleSelectAllClick}
                                onRequestSort={handleRequestSort}
                                rowCount={rows.length}
                                theme={theme}
                                selected={selected}
                            />
                            <TableBody>
                                {stableSort(rows, getComparator(order, orderBy))
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((row, index) => {
                                        const isItemSelected = isSelected(row.name);
                                        const labelId = `enhanced-table-checkbox-${index}`;

                                        return (
                                            <TableRow
                                                hover
                                                role="checkbox"
                                                aria-checked={isItemSelected}
                                                tabIndex={-1}
                                                key={index}
                                                selected={isItemSelected}
                                            >
                                                <TableCell
                                                    padding="checkbox"
                                                    sx={{ pl: 3 }}
                                                    onClick={(event) => handleClick(event, row.name)}
                                                >
                                                    <Checkbox
                                                        color="primary"
                                                        checked={isItemSelected}
                                                        inputProps={{
                                                            'aria-labelledby': labelId
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell
                                                    component="th"
                                                    id={labelId}
                                                    scope="row"
                                                    onClick={(event) => handleClick(event, row.name)}
                                                    sx={{ cursor: 'pointer', padding: 0, textAlign: 'center', fontSize: '0.8rem' }}
                                                >
                                                    <Typography
                                                        variant="subtitle1"
                                                        sx={{ color: theme.palette.mode === 'dark' ? 'grey.600' : 'grey.900' }}
                                                    >
                                                        {' '}
                                                        {row.id}{' '}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell
                                                    component="th"
                                                    id={labelId}
                                                    scope="row"
                                                    onClick={(event) => handleClick(event, row.name)}
                                                    sx={{ cursor: 'pointer', padding: 0, fontSize: '0.8rem' }}
                                                >
                                                    <Typography
                                                        variant="subtitle1"
                                                        sx={{
                                                            color: theme.palette.mode === 'dark' ? 'grey.600' : 'grey.900',
                                                            fontSize: '0.8rem'
                                                        }}
                                                    >
                                                        {' '}
                                                        {row.name}{' '}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="center" sx={{ padding: 0, fontSize: '0.8rem' }}>
                                                    {row.type === 'pdf' && <Chip label="pdf" size="small" chipcolor="warning" />}
                                                    {row.type === 'docx' && <Chip label="docx" size="small" chipcolor="primary" />}
                                                    {row.type === 'xml' && <Chip label="xml" size="small" chipcolor="secondary" />}
                                                    {row.type === 'xlsx' && <Chip label="xlsx" size="small" chipcolor="success" />}
                                                </TableCell>
                                                <TableCell align="center" sx={{ padding: 0, fontSize: '0.8rem' }}>
                                                    {row.crawl_status === 'Done' && <Chip label="Done" size="small" chipcolor="success" />}
                                                    {row.crawl_status === 'Processing' && (
                                                        <Chip label="Processing" size="small" chipcolor="primary" />
                                                    )}
                                                </TableCell>
                                                <TableCell align="center" sx={{ padding: 0, fontSize: '0.8rem' }}>
                                                    {row.index_status === 'Done' && <Chip label="Done" size="small" chipcolor="success" />}
                                                    {row.index_status === 'Processing' && (
                                                        <Chip label="Processing" size="small" chipcolor="primary" />
                                                    )}
                                                </TableCell>
                                                <TableCell align="center" sx={{ padding: 0, fontSize: '0.8rem' }}>
                                                    {row.created_date}
                                                </TableCell>
                                                <TableCell align="center" sx={{ padding: 0, fontSize: '0.8rem' }}>
                                                    {row.word_num}
                                                </TableCell>
                                                {/* <TableCell align="center" sx={{ padding: 0, fontSize: '0.8rem' }}>
                                                    <Tooltip title="Xóa">
                                                        <span>
                                                            <IconButton
                                                                color="secondary"
                                                                size="small"
                                                                onClick={() => handleDelete(row.name)}
                                                            >
                                                                <DeleteIcon sx={{ fontSize: '1.3rem' }} />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                </TableCell> */}
                                            </TableRow>
                                        );
                                    })}
                                {emptyRows > 0 && (
                                    <TableRow
                                        style={{
                                            height: 53 * emptyRows
                                        }}
                                    >
                                        <TableCell colSpan={6} />
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={rows.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                    <SimpleModal open={open} closeModal={() => setOpen(false)} />
                </div>
            )}
            {logOpen && <LogComponent onBack={handleBack} />}
            {metaDataOpen && <MetaDataComponent onBack={handleBack} />}
        </>
    );
};

export default TableUploadFile;
